import { Box, Typography } from '@mui/material';
import { usePrerenderReady, usePageMetadata } from '../../../seo/hooks';

const TermsAndConditions = () => {
    usePageMetadata({
        title: 'Terms and Conditions | Neurosynth Compose',
        description: 'Read the terms, conditions, and privacy policy for using the Neurosynth Compose platform.',
        canonicalPath: '/termsandconditions',
    });
    usePrerenderReady(true);

    return (
        <Box>
            <Typography variant="h5" mb="1rem" fontWeight="bold">
                Terms and Conditions
            </Typography>
            <Box mb="2rem">
                <Typography variant="body1" py="0.25rem">
                    Welcome to Neurosynth Compose! These Terms and Conditions govern your use of the Neurosynth Compose
                    website (hereinafter referred to as "the Website"). Please read these terms carefully before using
                    the Website.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Acceptance of Terms: By accessing or using the Website, you agree to be bound by these Terms and
                    Conditions. If you do not agree to these terms, please do not use the Website.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Description of Service: Neurosynth Compose provides a platform for users to create accounts and
                    upload neuroscience data for public use. Users may upload data, including but not limited to
                    neuroimaging data, metadata, and associated information.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    User Accounts: In order to use certain features of the Website, you may be required to create an
                    account. You are responsible for maintaining the confidentiality of your account and password and
                    for restricting access to your account. You agree to accept responsibility for all activities that
                    occur under your account.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    User Responsibilities: By uploading data to the Website, you represent and warrant that you have the
                    right to do so and that the data does not infringe upon the intellectual property rights or privacy
                    rights of any third party. You agree not to upload any data that is unlawful, defamatory, obscene,
                    or otherwise objectionable.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Intellectual Property: Users retain ownership of the data they upload to the Website. By uploading
                    data, users grant Neurosynth Compose a non-exclusive, royalty-free, worldwide license to use,
                    reproduce, modify, distribute, and display the data for the purposes of operating and improving the
                    Website.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Privacy: Neurosynth Compose respects the privacy of its users. Please review our Privacy Policy
                    (below) to understand how we collect, use, and disclose information.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Limitation of Liability: Neurosynth Compose shall not be liable for any indirect, incidental,
                    special, or consequential damages arising out of the use or inability to use the Website.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Indemnification: You agree to indemnify and hold harmless Neurosynth Compose and its affiliates,
                    officers, agents, and employees from any claim or demand, including reasonable attorneys' fees, made
                    by any third party due to or arising out of your use of the Website, your violation of these Terms
                    and Conditions, or your violation of any rights of another party.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Modifications to Terms: Neurosynth Compose reserves the right to modify these Terms and Conditions
                    at any time without prior notice. Your continued use of the Website after any such changes
                    constitutes your acceptance of the new Terms and Conditions.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Governing Law: These Terms and Conditions shall be governed by and construed in accordance with the
                    laws of The United States of America. Any dispute arising out of or relating to these terms shall be
                    subject to the exclusive jurisdiction of the courts located in The United States of America.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    Contact Information: If you have any questions or concerns about these Terms and Conditions, please
                    contact us at neurosynthorg@gmail.com.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    By using the Neurosynth Compose website, you agree to abide by these Terms and Conditions.
                </Typography>
            </Box>
            <Typography variant="h5" mb="1rem" fontWeight="bold">
                Privacy Policy
            </Typography>
            <Box>
                <Typography variant="body1" py="0.25rem">
                    This Privacy Policy describes how Neurosynth Compose ("we", "us", or "our") collects, uses, and
                    discloses information when you use the Neurosynth Compose website (the "Website").
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Information We Collect:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    a. User-Provided Information: When you create an account on the Website, we collect information such
                    as your username, email address, and any other information you choose to provide.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    b. Uploaded Data: When you upload data to the Website, including neuroimaging data and associated
                    metadata, we collect and store this information.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    c. Automatically Collected Information: We may automatically collect certain information about your
                    device and usage of the Website, including your IP address, browser type, operating system, and
                    pages visited.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Use of Information
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    a. We use the information we collect to operate and improve the Website, including providing
                    personalized content and recommendations.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    b. Uploaded data may be made publicly accessible for academic and research purposes, in accordance
                    with the terms and conditions of the Website.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    c. We may use your email address to send you administrative communications, such as account
                    notifications and updates to our terms and policies.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Disclosure of Information:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    a. We may disclose your information to third-party service providers who assist us in operating the
                    Website, subject to confidentiality agreements.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    b. Uploaded data may be shared publicly on the Website or with other users for academic and research
                    purposes.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    c. We may disclose information in response to a subpoena, court order, or other legal process, or to
                    protect our rights or the rights of others.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Data Retention:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    We retain the information we collect for as long as necessary to fulfill the purposes outlined in
                    this Privacy Policy, unless a longer retention period is required or permitted by law.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Security
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    We take reasonable measures to protect the security of your information, but please be aware that no
                    method of transmission over the internet or electronic storage is completely secure.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Your Choices
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    You may update or delete your account information at any time by contacting the neurosynth-compose
                    team. Please note that certain information may be retained in our backup systems for a limited
                    period of time.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Third-Party Links:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    The Website may contain links to third-party websites or services that are not owned or controlled
                    by us. We are not responsible for the privacy practices or content of these third-party sites.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Children's Privacy:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    The Website is not intended for children under the age of 13, and we do not knowingly collect
                    personal information from children under 13. If you believe that we have inadvertently collected
                    information from a child under 13, please contact us immediately.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Changes to this Privacy Policy:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting
                    the new Privacy Policy on this page.
                </Typography>
                <Typography variant="h6" py="0.25rem">
                    Contact Us:
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    If you have any questions or concerns about this Privacy Policy, please contact us at
                    neurosynthorg@gmail.com.
                </Typography>
                <Typography variant="body1" py="0.25rem">
                    By using the Neurosynth Compose website, you consent to the collection, use, and disclosure of your
                    information as described in this Privacy Policy. Thank you for using Neurosynth Compose!
                </Typography>
            </Box>
        </Box>
    );
};

export default TermsAndConditions;
